import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { t } = useI18n();
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      const me = await refreshMe();
      if (me?.role !== "admin") {
        setError(t("admin.not_authorized"));
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-[#1E2638] bg-[#07090E] px-3 py-2.5 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none focus:shadow-[0_0_12px_rgba(0,255,135,0.2)]";

  return (
    <div className="flex justify-center pt-16" data-testid="admin-login-page">
      <form onSubmit={submit} className="hud-card w-full max-w-sm rounded-xl p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#00FF87]/40 bg-[#00FF87]/15 shadow-[0_0_14px_rgba(0,255,135,0.3)]">
            <Lock className="h-4 w-4 text-[#00FF87]" />
          </span>
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{t("admin.login_title")}</h1>
        </div>
        <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.email")}</label>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          className={`${inputCls} mb-4`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.password")}</label>
        <input
          data-testid="admin-password-input"
          type="password"
          required
          className={`${inputCls} mb-5`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p className="mb-4 rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-[#00FF87] px-4 py-2.5 font-mono2 text-xs font-bold uppercase tracking-wider text-[#07090E] transition-all hover:shadow-[0_0_20px_rgba(0,255,135,0.5)] disabled:opacity-50"
        >
          {busy ? t("common.loading") : t("admin.sign_in")}
        </button>
      </form>
    </div>
  );
}
